export interface ChipOption {
  value: string;
  label: string;
  /** A second, smaller line under the label, such as a range. */
  detail?: string;
  /** Tooltip for the chip, for a blurb too long to print on it. */
  title?: string;
}

interface OptionChipsProps {
  legend: string;
  hint?: string;
  options: readonly ChipOption[];
  selected: readonly string[];
  onToggle: (value: string) => void;
  disabled?: boolean;
  error?: string | null;
  /**
   * `multi` (default) renders checkboxes — several options can be on at once.
   * `single` renders one radio group, so arrow keys move the choice.
   */
  mode?: 'multi' | 'single';
  /** Radio group name, used in `single` mode. Falls back to the legend. */
  name?: string;
  /**
   * Values shown but not choosable right now. The reason belongs in `hint`,
   * since a disabled control cannot explain itself.
   */
  unavailable?: readonly string[];
}

/**
 * Chip group backed by real checkboxes or radios, so keyboard and screen
 * reader behaviour comes for free.
 */
export function OptionChips({
  legend,
  hint,
  options,
  selected,
  onToggle,
  disabled = false,
  error = null,
  mode = 'multi',
  name,
  unavailable = [],
}: OptionChipsProps) {
  const isSingle = mode === 'single';

  return (
    <fieldset className="chip-group" disabled={disabled}>
      <legend className="field__label">{legend}</legend>
      {hint ? <p className="field__hint">{hint}</p> : null}

      <div className="chip-group__options">
        {options.map((option) => {
          const isSelected = selected.includes(option.value);
          const isUnavailable = unavailable.includes(option.value);
          const className = isUnavailable
            ? 'chip chip--unavailable'
            : isSelected
              ? 'chip chip--selected'
              : 'chip';
          return (
            <label className={className} key={option.value} title={option.title}>
              <input
                className="chip__input"
                type={isSingle ? 'radio' : 'checkbox'}
                name={isSingle ? (name ?? legend) : undefined}
                value={option.value}
                checked={isSelected}
                disabled={isUnavailable}
                onChange={() => onToggle(option.value)}
              />
              {option.detail ? (
                <span className="chip__text">
                  <span>{option.label}</span>
                  <span className="chip__detail">{option.detail}</span>
                </span>
              ) : (
                <span>{option.label}</span>
              )}
            </label>
          );
        })}
      </div>

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
